import React, { useState } from 'react';
import { Search, Music, Loader2, X, CheckCircle } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { useSpotifySearch } from '@/hooks/useSpotifySearch';
import { SpotifyTrack } from '@/services/spotifyService';

interface SpotifyTrackData {
  title: string;
  artist: string;
  album?: string;
  duration?: string;
  albumCoverUrl?: string;
}

interface SpotifySongSearchProps {
  onTrackSelect: (track: SpotifyTrackData) => void;
  selectedTrack: SpotifyTrack | null;
  onClear: () => void;
  placeholder?: string;
  className?: string;
  label?: string;
  showPreview?: boolean;
  disabled?: boolean;
}

const SpotifySongSearch: React.FC<SpotifySongSearchProps> = ({
  onTrackSelect,
  selectedTrack,
  onClear,
  placeholder = "Search for a song...",
  className = "",
  label = "Search for Song",
  showPreview = true,
  disabled = false
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const { tracks, isLoading, error, searchTracks, clearResults } = useSpotifySearch();

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (disabled) return;
    
    const value = e.target.value;
    setSearchInput(value);
    
    if (value.trim()) {
      searchTracks(value);
      setShowDropdown(true);
    } else {
      clearResults();
      setShowDropdown(false);
    }
  };

  const formatDuration = (durationMs: number): string => {
    const minutes = Math.floor(durationMs / 60000);
    const seconds = Math.floor((durationMs % 60000) / 1000);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleTrackSelect = (track: SpotifyTrack) => {
    const trackData: SpotifyTrackData = {
      title: track.name,
      artist: track.artists[0]?.name || '',
      album: track.album.name,
      duration: track.duration_ms ? formatDuration(track.duration_ms) : undefined,
      albumCoverUrl: track.album.images[0]?.url
    };

    onTrackSelect(trackData);
    setSearchInput(`${track.name} - ${track.artists[0]?.name}`);
    setShowDropdown(false);
    clearResults();
  };

  const handleClear = () => {
    if (disabled) return;
    
    setSearchInput('');
    setShowDropdown(false);
    clearResults();
    onClear();
  };

  return (
    <div className={`space-y-4 ${className}`}>
      {/* Search Section */}
      <div className="space-y-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
        <div className="flex items-center gap-2">
          <Music className="h-5 w-5 text-blue-600" />
          <Label className="text-blue-800 font-medium">{label}</Label>
          {selectedTrack && (
            <CheckCircle className="h-4 w-4 text-green-600" />
          )}
        </div>
        <p className="text-sm text-blue-600">
          Search Spotify to auto-populate song details like artist, album, and duration.
        </p>
        
        <div className="relative">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              type="text"
              placeholder={placeholder}
              value={searchInput}
              onChange={handleInputChange}
              className="pl-10 pr-10"
              autoComplete="off"
              disabled={disabled}
            />
            {(searchInput || selectedTrack) && !disabled && (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClear}
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0 hover:bg-muted"
              >
                <X className="h-3 w-3" />
              </Button>
            )}

            {/* Loading indicator */}
            {isLoading && (
              <div className="absolute right-12 top-1/2 -translate-y-1/2">
                <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
              </div>
            )}
          </div>

          {/* Search results dropdown */}
          {showDropdown && (tracks.length > 0 || error) && (
            <Card className="absolute top-full left-0 right-0 z-50 mt-1 max-h-80 overflow-y-auto shadow-lg">
              <CardContent className="p-0">
                {error ? (
                  <div className="p-4 text-center text-sm text-red-600">
                    <Music className="mx-auto mb-2 h-8 w-8 text-red-400" />
                    <p>Unable to search Spotify</p>
                    <p className="text-xs text-muted-foreground mt-1">{error}</p>
                  </div>
                ) : tracks.length === 0 ? (
                  <div className="p-4 text-center text-sm text-muted-foreground">
                    <Music className="mx-auto mb-2 h-8 w-8" />
                    <p>No songs found</p>
                    <p className="text-xs mt-1">Try a different search term</p>
                  </div>
                ) : (
                  <div className="py-2">
                    {tracks.map((track) => (
                      <button
                        key={track.id}
                        onClick={() => handleTrackSelect(track)}
                        className="w-full px-4 py-3 text-left hover:bg-muted/50 transition-colors border-none bg-transparent cursor-pointer"
                      >
                        <div className="flex items-start gap-3">
                          {/* Album cover */}
                          <div className="shrink-0">
                            {track.album.images[0] ? (
                              <img
                                src={track.album.images[2]?.url || track.album.images[0]?.url}
                                alt={track.album.name}
                                className="h-12 w-12 rounded object-cover"
                              />
                            ) : (
                              <div className="h-12 w-12 rounded bg-muted flex items-center justify-center">
                                <Music className="h-6 w-6 text-muted-foreground" />
                              </div>
                            )}
                          </div>

                          {/* Track info */}
                          <div className="flex-1 min-w-0">
                            <p className="font-medium text-sm leading-5 truncate">
                              {track.name}
                            </p>
                            <p className="text-xs text-muted-foreground leading-4 truncate">
                              {track.artists.map(artist => artist.name).join(', ')}
                            </p>
                            <p className="text-xs text-muted-foreground leading-4 truncate">
                              {track.album.name}
                            </p>
                          </div>

                          {/* Duration */}
                          {track.duration_ms && (
                            <div className="shrink-0 text-xs text-muted-foreground">
                              {formatDuration(track.duration_ms)}
                            </div>
                          )}
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      {/* Selected Track Preview */}
      {showPreview && selectedTrack && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertDescription>
            <div className="flex items-center gap-3">
              {selectedTrack.album.images[0] && (
                <img
                  src={selectedTrack.album.images[2]?.url || selectedTrack.album.images[0]?.url}
                  alt={selectedTrack.album.name}
                  className="h-10 w-10 rounded object-cover"
                />
              )}
              <div className="flex-1">
                <p className="font-medium text-green-800">
                  Selected: {selectedTrack.name}
                </p>
                <p className="text-sm text-green-600">
                  by {selectedTrack.artists[0]?.name} • {selectedTrack.album.name}
                </p>
              </div>
            </div>
          </AlertDescription>
        </Alert>
      )}
    </div>
  );
};

export default SpotifySongSearch; 